import { useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import CustomerInformation from "@/components/CustomerInformation"
import CustomerDevices from "@/components/CustomerDevices"
import CustomerDetailHeader from "@/components/CustomerDetailHeader"
import useGetCustomerDetail from "@/hooks/api/use-get-customer-detail"
import useGetWaterConsumption from "@/hooks/api/use-get-water-consumption"
import { LoadingSpinner } from "@/components/ui/loading-spinner"
import useUpdateCustomer from "@/hooks/api/use-update-customer"
import { useQueryClient } from "@tanstack/react-query"
import { useGetCustomerList } from "@/hooks/api/use-get-customer-list"
import { useToast } from "@/components/ui/use-toast"
import { DeleteCustomerModal } from "@/components/DeleteCustomerModal"
import useDeleteCustomer from "@/hooks/api/use-delete-customer"
import { useAuth } from "@/hooks/useAuth"
import useCurrentUser from "@/hooks/use-current-user"
import { hasPermission } from "@/lib/admin-permissions"

export default function CustomerDetailPage() {
  const { customerId, councilId } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const { user } = useAuth()
  const { hasRole } = useCurrentUser()
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const scopedCouncilId = councilId ? Number(councilId) : undefined
  const { data: customer, isLoading, isError, error } = useGetCustomerDetail(councilId || "", customerId || "")
  const { data: waterConsumption } = useGetWaterConsumption(councilId || "", customerId || "")
  const invalidateCustomers = () => {
    queryClient.invalidateQueries({
      queryKey: ["customerDetail", councilId || "", customerId || ""],
    })
    queryClient.invalidateQueries({ queryKey: [useGetCustomerList.QUERY_KEY] })
  }

  const { mutate: updateCustomer, isPending: isUpdatingCustomer } = useUpdateCustomer({
    onSuccess: () => {
      invalidateCustomers()
      toast({ title: "Success", description: "Customer status updated" })
    },
    onError: (message) => {
      toast({ title: "Error", description: message, variant: "destructive" })
    },
  })

  const { mutate: deleteCustomer, isPending: isDeletingCustomer } = useDeleteCustomer({
    onSuccess: () => {
      invalidateCustomers()
      toast({ title: "Success", description: "Customer has been deleted" })
      navigate(councilId ? `/council/${councilId}/customers` : "/customers")
    },
  })

  if (isLoading) {
    return <LoadingSpinner />
  }

  if (isError) {
    const axiosError = error as { response?: { status?: number } }
    if (axiosError?.response?.status === 410) {
      return (
        <div className="flex flex-col items-center justify-center py-16 space-y-4">
          <div className="text-muted-foreground text-center">
            <p className="text-lg font-medium">This customer account has been deleted</p>
            <p className="text-sm mt-1">Deleted accounts cannot be accessed.</p>
          </div>
          <button
            type="button"
            onClick={() => navigate(councilId ? `/council/${councilId}/customers` : "/customers")}
            className="text-primary underline"
          >
            Back to customers
          </button>
        </div>
      )
    }
    return <div>Customer not found</div>
  }

  if (!customer) {
    return <div>Customer not found</div>
  }

  const isOwnedCustomer = customer.ownedActorId != null && customer.ownedActorId === user?.id
  const canMutateCustomer =
    hasRole("super_admin") ||
    hasPermission(user?.permissions, "customers", "write_all", scopedCouncilId) ||
    (hasPermission(user?.permissions, "customers", "write_own", scopedCouncilId) && isOwnedCustomer)

  return (
    <div className="space-y-6 animate-fade-in">
      <CustomerDetailHeader />
      <CustomerInformation
        customer={customer}
        waterConsumption={waterConsumption ?? undefined}
        canWriteCustomer={
          canMutateCustomer ||
          hasPermission(user?.permissions, "customers", "write_own", scopedCouncilId)
        }
        canMutateCustomer={canMutateCustomer}
        isUpdatingStatus={isUpdatingCustomer}
        isDeletingCustomer={isDeletingCustomer}
        onDeleteCustomer={() => setIsDeleteModalOpen(true)}
        onSuspendedChange={(checked) => {
          if (!councilId || !customerId) return
          updateCustomer({
            councilId,
            customerId,
            req: { status: checked ? "SUSPENDED" : "ACTIVE" },
          })
        }}
      />
      <CustomerDevices customerId={customerId || ""} councilId={councilId || ""} />
      <DeleteCustomerModal
        open={isDeleteModalOpen}
        onOpenChange={setIsDeleteModalOpen}
        customerName={customer.name || ""}
        onConfirm={() => {
          if (!councilId || !customerId) return
          deleteCustomer({ councilId, customerId })
          setIsDeleteModalOpen(false)
        }}
      />
    </div>
  )
}
